import React, { memo } from 'react'
import { Routes, Route, useParams } from 'react-router-dom'
import { Icon, IconName } from '@unbounded/unbounded-components'
import { ConsortiumDetailsFragment } from '~api/fragments/ConsortiumDetails.generated'
import Image from '~components/Image'
import { StyledDetails, StyledDetailsHeader, StyledTabs, DetailsOverview } from '~components/Details'
import consortiumsIcon from '~assets/icons/consortiums.svg'
import { NestedPaths } from '../Consortiums.consts'
import { OrganizationsList } from './OrganizationsList'

interface Props {
  consortium: ConsortiumDetailsFragment
}

const ConsortiumDetails: React.FC<Props> = memo(({ consortium }) => {
  const params = useParams()
  const { name, frameworks, description, logo, organizations, socialUrls } = consortium
  const frameworkNames = frameworks.length ? frameworks.map(({ name: title }) => title).join(', ') : ''

  const organizationsQuantity = organizations.length

  const OverviewComponent = <DetailsOverview {...{ description, socialUrls }} />
  const OrganizationsComponent = organizationsQuantity !== 0 ? <OrganizationsList items={organizations} /> : null

  return (
    <>
      <StyledDetailsHeader.Container>
        <StyledDetailsHeader.InfoContainer>
          <StyledDetailsHeader.ImageContainer isBright={logo?.isBright}>
            <Image src={logo?.url} alt={name} />
          </StyledDetailsHeader.ImageContainer>

          <StyledDetailsHeader.Section>
            <StyledDetailsHeader.Title>{name}</StyledDetailsHeader.Title>

            {frameworkNames && (
              <StyledDetailsHeader.SubHeader>
                <Icon src={consortiumsIcon} />
                <StyledDetailsHeader.SubTitle>{frameworkNames}</StyledDetailsHeader.SubTitle>
              </StyledDetailsHeader.SubHeader>
            )}
          </StyledDetailsHeader.Section>
        </StyledDetailsHeader.InfoContainer>

        {/* TODO Share button */}
      </StyledDetailsHeader.Container>

      {/* TODO make Tabs shared and move to unbounded-components */}
      <StyledTabs.Container>
        <StyledTabs.LinksList>
          <StyledTabs.Link $isActive={params['*'] === 'overview'} to={NestedPaths.overview}>
            <StyledTabs.LinkIcon name={IconName.file_eye} />
            Overview
          </StyledTabs.Link>
          {organizationsQuantity !== 0 && (
            <StyledTabs.Link $isActive={params['*'] === 'organizations'} to={NestedPaths.organizations}>
              <StyledTabs.LinkIcon name={IconName.network} />
              Organizations ({organizationsQuantity})
            </StyledTabs.Link>
          )}
        </StyledTabs.LinksList>

        <StyledTabs.Content>
          <Routes>
            <Route
              index
              element={
                OverviewComponent || <StyledDetails.Placeholder>We are apparently missing this consortium data, sorry.</StyledDetails.Placeholder>
              }
            />
            <Route path={NestedPaths.overview} element={OverviewComponent} />
            {OrganizationsComponent && <Route path={NestedPaths.organizations} element={OrganizationsComponent} />}
          </Routes>
        </StyledTabs.Content>
      </StyledTabs.Container>
    </>
  )
})

ConsortiumDetails.displayName = 'ConsortiumDetails'
export default ConsortiumDetails
